// /src/pages/admin/clientes/MapaClientes.jsx
// Mapa de clientes. Carga solo [id, lat, lng, nombre] (GET /customers/mapa) y pide el detalle del cliente al hacer clic.

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  GoogleMap,
  Marker,
  MarkerClusterer,
  InfoWindow,
} from "@react-google-maps/api";
import axios from "../../../api/axios";
import { Search, X, Loader2, MapPin } from "lucide-react";

const containerStyle = {
  width: "100%",
  height: "80vh",
};

const CENTRO_POR_DEFECTO = { lat: -1.2884, lng: -78.6167 };

const tieneCoordenadas = (c) =>
  c &&
  c.latitud &&
  c.longitud &&
  !isNaN(parseFloat(c.latitud)) &&
  !isNaN(parseFloat(c.longitud));

const aPunto = (c) => [
  c.id,
  parseFloat(c.latitud),
  parseFloat(c.longitud),
  `${(c.apellidos || "").trim()} ${(c.nombres || "").trim()}`.trim(),
];

const claseEstado = (estado) =>
  estado === "ACTIVO"
    ? "bg-green-100 text-green-800"
    : estado === "SUSPENDIDO"
    ? "bg-yellow-100 text-yellow-800"
    : "bg-gray-100 text-gray-800";

const formatAddress = (cliente) => {
  const parts = [];
  if (cliente.direccion) parts.push(cliente.direccion);
  if (cliente.ciudad) parts.push(cliente.ciudad);
  if (cliente.sector) parts.push(cliente.sector);
  return parts.join(", ") || "Sin dirección";
};

// Capa de marcadores memorizada: escribir en el buscador o abrir un globo NO vuelve a dibujar miles de marcadores
const CapaMarcadores = React.memo(function CapaMarcadores({ puntos, onSelect }) {
  return (
    <MarkerClusterer>
      {(clusterer) =>
        puntos.map((p) => (
          <Marker
            key={p[0]}
            position={{ lat: p[1], lng: p[2] }}
            clusterer={clusterer}
            onClick={() => onSelect(p)}
          />
        ))
      }
    </MarkerClusterer>
  );
});

export default function MapaClientes() {
  const [todos, setTodos] = useState([]);
  const [visibles, setVisibles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const detalleSeq = useRef(0);

  const center = useMemo(() => {
    if (mapCenter) return mapCenter;
    if (visibles.length > 0) return { lat: visibles[0][1], lng: visibles[0][2] };
    return CENTRO_POR_DEFECTO;
  }, [visibles, mapCenter]);

  // Carga inicial: solo id, coordenadas y nombre
  useEffect(() => {
    let vivo = true;
    const cargar = async () => {
      try {
        const res = await axios.get("/customers/mapa");
        if (!vivo) return;
        const puntos = Array.isArray(res.data && res.data.puntos) ? res.data.puntos : [];
        setTodos(puntos);
        setVisibles(puntos);
      } catch (err) {
        console.error("Error al cargar el mapa de clientes:", err);
        if (vivo) setErrorCarga("No se pudo cargar el mapa de clientes");
      } finally {
        if (vivo) setLoading(false);
      }
    };
    cargar();
    return () => {
      vivo = false;
      clearTimeout(debounceRef.current);
    };
  }, []);

  // Búsqueda en el servidor (con debounce)
  const handleSearch = (query) => {
    setSearchTerm(query);
    setShowResults(true);
    clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setSearchResults([]);
      setVisibles(todos);
      setMapCenter(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await axios.get("/customers/search", {
          params: { search: query, limit: 50 },
        });
        const resultados = (response.data || []).filter(tieneCoordenadas);
        setSearchResults(resultados);
        if (resultados.length > 0) {
          setVisibles(resultados.map(aPunto));
          setMapCenter({
            lat: parseFloat(resultados[0].latitud),
            lng: parseFloat(resultados[0].longitud),
          });
        } else {
          setVisibles(todos);
          setMapCenter(null);
        }
      } catch (error) {
        console.error("Error al buscar clientes:", error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const clearSearch = () => {
    clearTimeout(debounceRef.current);
    setSearchTerm("");
    setSearchResults([]);
    setVisibles(todos);
    setShowResults(false);
    setMapCenter(null);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Seleccionar un cliente de los resultados de búsqueda (ya trae todos sus datos)
  const selectCliente = (cliente) => {
    detalleSeq.current += 1;
    setSelected(cliente);
    setShowResults(false);
    setMapCenter({
      lat: parseFloat(cliente.latitud),
      lng: parseFloat(cliente.longitud),
    });
    setVisibles([aPunto(cliente)]);
    setSearchTerm(`${cliente.nombres} ${cliente.apellidos}`);
  };

  const resetMap = () => {
    clearTimeout(debounceRef.current);
    detalleSeq.current += 1;
    setVisibles(todos);
    setMapCenter(null);
    setSearchTerm("");
    setSearchResults([]);
    setShowResults(false);
    setSelected(null);
  };

  // Clic en un marcador: se muestra el nombre al instante y el detalle se pide al servidor
  const abrirPunto = useCallback(async (p) => {
    detalleSeq.current += 1;
    const mio = detalleSeq.current;
    setSelected({ id: p[0], latitud: p[1], longitud: p[2], nombres: p[3], apellidos: "", cargando: true });
    try {
      const res = await axios.get(`/customers/${p[0]}`);
      if (detalleSeq.current === mio) {
        setSelected({ ...res.data, latitud: p[1], longitud: p[2] });
      }
    } catch (error) {
      console.error("Error al cargar el detalle del cliente:", error);
      if (detalleSeq.current === mio) {
        setSelected((s) => (s && s.id === p[0] ? { ...s, cargando: false, errorDetalle: true } : s));
      }
    }
  }, []);

  const cerrarGlobo = useCallback(() => {
    detalleSeq.current += 1;
    setSelected(null);
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto p-4 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-4">Cargando mapa de clientes...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
        <h1 className="text-xl font-bold">
          Mapa de Clientes Instalados ({visibles.length})
        </h1>

        {/* Buscador */}
        <div className="relative w-full md:w-96">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar por nombre, cédula o dirección..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setShowResults(true)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 h-4 w-4 animate-spin" />
            )}
          </div>

          {/* Resultados de búsqueda */}
          {showResults && searchTerm && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
              {searching ? (
                <div className="p-4 text-center text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  <p className="mt-1 text-sm">Buscando...</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <p>No se encontraron clientes con ubicación</p>
                  <p className="text-xs mt-1">Intenta con otro término de búsqueda</p>
                </div>
              ) : (
                <ul>
                  {searchResults.map((cliente) => (
                    <li
                      key={cliente.id}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                      onClick={() => selectCliente(cliente)}
                    >
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {cliente.nombres} {cliente.apellidos}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {cliente.identificacion} • {formatAddress(cliente)}
                          </p>
                          <p className="text-xs text-gray-400">
                            Plan: {cliente.plan || "N/A"} • Estado:
                            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${claseEstado(cliente.estado)}`}>
                              {cliente.estado}
                            </span>
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                  <li className="px-4 py-2 bg-gray-50 text-center border-t border-gray-200">
                    <button
                      onClick={resetMap}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Mostrar todos los clientes ({todos.length})
                    </button>
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {errorCarga && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{errorCarga}</div>
      )}

      {/* Mapa */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={visibles.length === 1 ? 16 : 12}
        onLoad={(map) => {
          mapRef.current = map;
        }}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      >
        <CapaMarcadores puntos={visibles} onSelect={abrirPunto} />

        {selected && (
          <InfoWindow
            position={{
              lat: parseFloat(selected.latitud),
              lng: parseFloat(selected.longitud),
            }}
            onCloseClick={cerrarGlobo}
          >
            <div className="p-2 max-w-xs">
              <h2 className="font-bold text-lg">
                {selected.nombres} {selected.apellidos}
              </h2>
              {selected.cargando ? (
                <p className="text-sm mt-1 text-gray-500">Cargando datos...</p>
              ) : (
                <>
                  {selected.errorDetalle && (
                    <p className="text-sm mt-1 text-red-600">No se pudo cargar el detalle</p>
                  )}
                  {selected.identificacion && (
                    <p className="text-sm mt-1">
                      <span className="font-semibold">ID:</span> {selected.identificacion}
                    </p>
                  )}
                  <p className="text-sm">
                    <span className="font-semibold">Dirección:</span> {formatAddress(selected)}
                  </p>
                  <p className="text-sm">
                    <span className="font-semibold">Plan:</span> {selected.plan || "N/A"}
                  </p>
                  {selected.estado && (
                    <p className="text-sm">
                      <span className="font-semibold">Estado:</span>
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${claseEstado(selected.estado)}`}>
                        {selected.estado}
                      </span>
                    </p>
                  )}
                  {selected.ip_asignada && (
                    <p className="text-sm">
                      <span className="font-semibold">IP:</span> {selected.ip_asignada}
                    </p>
                  )}
                </>
              )}
              <div className="mt-2 flex gap-2">
                <button
                  className="flex-1 bg-blue-500 text-white text-sm py-1.5 px-2 rounded hover:bg-blue-600 transition-colors"
                  onClick={() => window.open(
                    `https://www.google.com/maps/search/?api=1&query=${selected.latitud},${selected.longitud}`,
                    "_blank"
                  )}
                >
                  Ver en Maps
                </button>
                <button
                  className="flex-1 bg-gray-500 text-white text-sm py-1.5 px-2 rounded hover:bg-gray-600 transition-colors"
                  onClick={() => {
                    setVisibles([aPunto(selected)]);
                    setMapCenter({
                      lat: parseFloat(selected.latitud),
                      lng: parseFloat(selected.longitud),
                    });
                    cerrarGlobo();
                  }}
                >
                  Centrar
                </button>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Estadísticas rápidas */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <p className="text-sm text-gray-500">Total clientes</p>
          <p className="text-2xl font-bold text-blue-600">{todos.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <p className="text-sm text-gray-500">Mostrando en mapa</p>
          <p className="text-2xl font-bold text-green-600">{visibles.length}</p>
        </div>
        {searchTerm && searchResults.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200 md:col-span-2">
            <p className="text-sm text-gray-500">Búsqueda activa</p>
            <p className="text-sm font-medium text-gray-700 truncate">
              "{searchTerm}" - {searchResults.length} resultados con ubicación
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
